import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, CheckCircle2, Clock, BookPlus, Trash2, Pencil, Plus, ShieldCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { PROBLEMS, LEVELS, LOGO_URL, TOPICS } from "@/constants/categories";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { setNoIndex } from "@/utils/seo";
import { AdminSecurity } from "./AdminSecurity";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API, withCredentials: true });

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

export default function AdminDashboard() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [tools, setTools] = useState([]);
  const [toolEdit, setToolEdit] = useState(null);
  const [filterCat, setFilterCat] = useState("all");
  const [filterLevel, setFilterLevel] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [answers, setAnswers] = useState({});
  const [publish, setPublish] = useState(null);
  const [overview, setOverview] = useState(null);
  const [showSecurity, setShowSecurity] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (filterCat !== "all") params.problem = filterCat;
      if (filterLevel !== "all") params.level = filterLevel;
      if (filterStatus !== "all") params.status = filterStatus;
      const [s, q, f, t, o] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/questions", { params }),
        api.get("/faqs"),
        api.get("/tools"),
        api.get("/admin/stats/overview"),
      ]);
      setStats(s.data);
      setQuestions(q.data);
      setFaqs(f.data);
      setTools(t.data);
      setOverview(o.data);
    } catch (err) {
      if (err.response?.status === 401) {
        setUser(false);
        navigate("/admin");
      } else {
        toast.error("Errore nel caricamento dei dati");
      }
    }
  }, [filterCat, filterLevel, filterStatus, navigate, setUser]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    document.title = "Dashboard | HairHelp";
    setNoIndex();
  }, []);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
    navigate("/admin");
  };

  const markAnswered = async (q) => {
    try {
      const answer = answers[q.id]?.trim();
      await api.patch(`/admin/questions/${q.id}`, { status: "answered", answer: answer || q.answer });
      toast.success("Domanda segnata come risposta");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const markPending = async (q) => {
    try {
      await api.patch(`/admin/questions/${q.id}`, { status: "pending" });
      toast.success("Domanda rimessa in attesa");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const publishFaq = async () => {
    try {
      const payload = {
        level: publish.level,
        category: publish.category,
        question: publish.question,
        answer: publish.answer,
        related_ids: publish.relatedIds,
        tags: publish.tags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      if (publish.editId) {
        await api.patch(`/admin/faqs/${publish.editId}`, payload);
        toast.success("FAQ aggiornata");
      } else {
        await api.post("/admin/faqs", { ...payload, question_id: publish.questionId || undefined });
        toast.success("FAQ pubblicata");
      }
      setPublish(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveTool = async () => {
    try {
      await api.patch(`/admin/tools/${toolEdit.id}`, {
        name: toolEdit.name,
        purpose: toolEdit.purpose,
        usage: toolEdit.usage,
        avoid: toolEdit.avoid,
        related_ids: toolEdit.relatedIds,
      });
      toast.success("Strumento aggiornato");
      setToolEdit(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteFaq = async (id) => {
    try {
      await api.delete(`/admin/faqs/${id}`);
      toast.success("FAQ eliminata");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const totalQuestions = stats.reduce((a, s) => a + s.total, 0);
  const totalPending = stats.reduce((a, s) => a + s.pending, 0);

  return (
    <div className="min-h-screen bg-background">
      <AdminSecurity
        open={showSecurity}
        onOpenChange={setShowSecurity}
        onPasswordChanged={() => { setUser(false); navigate("/admin"); }}
      />
      <header className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="HairHelp" className="h-9 w-auto" />
            <Badge variant="secondary" className="ml-2">Admin</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowSecurity(true)} data-testid="open-security-button" className="gap-2">
              <ShieldCheck className="h-4 w-4" /> Sicurezza
            </Button>
            <Button variant="outline" size="sm" onClick={logout} data-testid="logout-button" className="gap-2">
              <LogOut className="h-4 w-4" /> Esci
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-8" data-testid="admin-dashboard">
        <h1 className="font-heading text-2xl font-semibold tracking-tight text-foreground" data-testid="dashboard-title">
          Dashboard
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {totalQuestions} domande totali · <span className="font-semibold text-foreground">{totalPending} in attesa</span>
        </p>

        {/* Contatori per categoria */}
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="category-counters">
          {stats.map((s) => (
            <button
              key={s.category}
              onClick={() => setFilterCat(filterCat === s.category ? "all" : s.category)}
              data-testid={`counter-${slug(s.category)}`}
              className={`rounded-xl border p-4 text-left transition-colors duration-200 ${
                filterCat === s.category ? "border-primary bg-secondary" : "border-border bg-card hover:bg-muted"
              }`}
            >
              <p className="text-xs font-semibold text-muted-foreground">{s.category}</p>
              <p className="mt-1 font-heading text-2xl font-bold text-foreground">{s.total}</p>
              {s.pending > 0 && (
                <p className="mt-0.5 text-xs font-semibold text-accent">{s.pending} in attesa</p>
              )}
            </button>
          ))}
        </div>

        {overview && (
          <div className="mt-6 rounded-xl border border-border bg-card p-5" data-testid="stats-overview">
            <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
              <p className="text-sm text-muted-foreground">
                Totale richieste: <span className="font-heading text-lg font-bold text-foreground" data-testid="stats-total">{overview.total}</span>
              </p>
              <p className="text-sm text-muted-foreground">
                Risposte inviate: <span className="font-semibold text-foreground">{overview.answered}</span>
              </p>
              <p className="text-sm text-muted-foreground">
                In attesa: <span className="font-semibold text-accent">{overview.pending}</span>
              </p>
            </div>
            {overview.by_day.length > 0 && (
              <div className="mt-5">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Andamento richieste (ultimi 30 giorni)</p>
                <div className="mt-2 flex h-16 items-end gap-1" data-testid="stats-trend">
                  {overview.by_day.map((d) => (
                    <div
                      key={d.date}
                      title={`${d.date}: ${d.count}`}
                      className="flex-1 rounded-sm bg-accent/70"
                      style={{ height: `${Math.max(8, (d.count / Math.max(...overview.by_day.map((x) => x.count))) * 100)}%` }}
                    />
                  ))}
                </div>
              </div>
            )}
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Per livello</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {overview.by_level.map((x) => (
                    <span key={x.key} className="rounded-full bg-muted px-3 py-1 text-xs font-semibold text-foreground">
                      {x.key}: {x.count}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Per tipo di sistema</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {overview.by_system_type.map((x) => (
                    <span key={x.key} className="rounded-full bg-muted px-3 py-1 text-xs font-semibold text-foreground">
                      {x.key}: {x.count}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <Tabs defaultValue="questions" className="mt-8">
          <TabsList data-testid="admin-tabs">
            <TabsTrigger value="questions" data-testid="tab-questions">Domande ({totalQuestions})</TabsTrigger>
            <TabsTrigger value="faqs" data-testid="tab-faqs">FAQ pubbliche ({faqs.length})</TabsTrigger>
            <TabsTrigger value="tools" data-testid="tab-tools">Strumenti ({tools.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="questions" className="mt-6">
            <div className="flex flex-wrap gap-3">
              <Select value={filterCat} onValueChange={setFilterCat}>
                <SelectTrigger className="w-56 bg-card" data-testid="filter-category">
                  <SelectValue placeholder="Tutti i problemi" />
                </SelectTrigger>
                <SelectContent className="bg-card">
                  <SelectItem value="all" data-testid="filter-category-all">Tutti i problemi</SelectItem>
                  {[...new Set([...PROBLEMS, ...TOPICS])].map((c) => (
                    <SelectItem key={c} value={c} data-testid={`filter-category-${slug(c)}`}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterLevel} onValueChange={setFilterLevel}>
                <SelectTrigger className="w-44 bg-card" data-testid="filter-level">
                  <SelectValue placeholder="Tutti i livelli" />
                </SelectTrigger>
                <SelectContent className="bg-card">
                  <SelectItem value="all" data-testid="filter-level-all">Tutti i livelli</SelectItem>
                  {LEVELS.map((l) => (
                    <SelectItem key={l.id} value={l.id} data-testid={`filter-level-${l.id}`}>{l.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-44 bg-card" data-testid="filter-status">
                  <SelectValue placeholder="Tutti gli stati" />
                </SelectTrigger>
                <SelectContent className="bg-card">
                  <SelectItem value="all" data-testid="filter-status-all">Tutti gli stati</SelectItem>
                  <SelectItem value="pending" data-testid="filter-status-pending">In attesa</SelectItem>
                  <SelectItem value="answered" data-testid="filter-status-answered">Risposte</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="mt-6 space-y-4" data-testid="questions-list">
              {questions.length === 0 && (
                <p className="rounded-xl bg-card p-8 text-center text-sm text-muted-foreground shadow-soft" data-testid="questions-empty">
                  Nessuna domanda trovata con questi filtri.
                </p>
              )}
              {questions.map((q) => (
                <div key={q.id} className="rounded-xl bg-card p-6 shadow-soft" data-testid={`question-card-${q.id}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="secondary">{LEVELS.find((l) => l.id === q.level)?.label || q.level || "—"}</Badge>
                    <Badge variant="outline">{q.problem || q.category}</Badge>
                    {q.system_type && <Badge variant="outline">{q.system_type}</Badge>}
                    {q.status === "answered" ? (
                      <Badge className="gap-1 bg-primary text-primary-foreground" data-testid={`status-badge-${q.id}`}>
                        <CheckCircle2 className="h-3 w-3" /> Risposta
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="gap-1 border-accent text-accent" data-testid={`status-badge-${q.id}`}>
                        <Clock className="h-3 w-3" /> In attesa
                      </Badge>
                    )}
                    {q.faq_published && <Badge variant="outline">In FAQ</Badge>}
                    <span className="ml-auto text-xs text-muted-foreground">{formatDate(q.created_at)}</span>
                  </div>
                  <p className="mt-3 text-sm font-semibold text-foreground">
                    {q.name || "Anonimo"} · <span className="font-normal text-muted-foreground">{q.email}</span>
                  </p>
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-foreground">{q.question}</p>

                  {q.status === "answered" && q.answer && (
                    <div className="mt-4 rounded-lg bg-muted p-4">
                      <p className="text-xs font-semibold text-muted-foreground">Risposta inviata</p>
                      <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">{q.answer}</p>
                    </div>
                  )}

                  <div className="mt-4 space-y-3">
                    {q.status === "pending" && (
                      <Textarea
                        placeholder="Scrivi qui la risposta che hai inviato via email (opzionale ma consigliato)…"
                        value={answers[q.id] ?? q.answer ?? ""}
                        onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                        rows={3}
                        data-testid={`answer-textarea-${q.id}`}
                        className="focus-visible:ring-primary"
                      />
                    )}
                    <div className="flex flex-wrap gap-2">
                      {q.status === "pending" ? (
                        <Button size="sm" onClick={() => markAnswered(q)} data-testid={`mark-answered-${q.id}`} className="gap-1.5">
                          <CheckCircle2 className="h-4 w-4" /> Segna come risposta
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => markPending(q)} data-testid={`mark-pending-${q.id}`} className="gap-1.5">
                          <Clock className="h-4 w-4" /> Rimetti in attesa
                        </Button>
                      )}
                      {!q.faq_published && (
                        <Button
                          size="sm"
                          variant="secondary"
                          data-testid={`publish-faq-${q.id}`}
                          className="gap-1.5"
                          onClick={() =>
                            setPublish({
                              editId: null,
                              questionId: q.id,
                              level: q.level || "basic",
                              category: q.problem || q.category || PROBLEMS[0],
                              question: q.question,
                              answer: answers[q.id]?.trim() || q.answer || "",
                              relatedIds: [],
                              tags: "",
                            })
                          }
                        >
                          <BookPlus className="h-4 w-4" /> Pubblica come FAQ
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="faqs" className="mt-6">
            <Button
              size="sm"
              data-testid="new-faq-button"
              className="mb-4 gap-1.5"
              onClick={() =>
                setPublish({
                  editId: null,
                  questionId: null,
                  level: "basic",
                  category: PROBLEMS[0],
                  question: "",
                  answer: "",
                  relatedIds: [],
                  tags: "",
                })
              }
            >
              <Plus className="h-4 w-4" /> Nuova FAQ
            </Button>
            <div className="space-y-4" data-testid="faqs-list">
              {faqs.length === 0 && (
                <p className="rounded-xl bg-card p-8 text-center text-sm text-muted-foreground shadow-soft" data-testid="faqs-empty">
                  Nessuna FAQ pubblicata. Pubblica la prima da una domanda risposta.
                </p>
              )}
              {faqs.map((f) => (
                <div key={f.id} className="flex items-start gap-4 rounded-xl bg-card p-6 shadow-soft" data-testid={`faq-card-${f.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="secondary">{LEVELS.find((l) => l.id === (f.level || "basic"))?.label || "Basic"}</Badge>
                      <Badge variant="outline">{f.category}</Badge>
                    </div>
                    <p className="mt-2 text-sm font-semibold text-foreground">{f.question}</p>
                    <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">{f.answer}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      data-testid={`edit-faq-${f.id}`}
                      onClick={() =>
                        setPublish({
                          editId: f.id,
                          questionId: null,
                          level: f.level || "basic",
                          category: f.category,
                          question: f.question,
                          answer: f.answer,
                          relatedIds: f.related_ids || [],
                          tags: (f.tags || []).join(", "),
                        })
                      }
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => deleteFaq(f.id)} data-testid={`delete-faq-${f.id}`} className="text-destructive hover:text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="tools" className="mt-6">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="tools-list">
              {tools.map((t) => (
                <div key={t.id} className="rounded-xl bg-card p-5 shadow-soft" data-testid={`tool-card-${t.id}`}>
                  <p className="text-sm font-semibold text-foreground">{t.name}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t.purpose ? t.purpose.slice(0, 90) + (t.purpose.length > 90 ? "…" : "") : "In preparazione"}
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-3 gap-1.5"
                    data-testid={`edit-tool-${t.id}`}
                    onClick={() =>
                      setToolEdit({
                        id: t.id,
                        name: t.name,
                        purpose: t.purpose || "",
                        usage: t.usage || "",
                        avoid: t.avoid || "",
                        relatedIds: t.related_ids || [],
                      })
                    }
                  >
                    <Pencil className="h-4 w-4" /> Modifica
                  </Button>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Dialog pubblicazione FAQ */}
      <Dialog open={!!publish} onOpenChange={(o) => !o && setPublish(null)}>
        <DialogContent className="bg-card sm:max-w-lg" data-testid="publish-faq-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {publish?.editId ? "Modifica FAQ" : publish?.questionId ? "Pubblica come FAQ" : "Nuova FAQ"}
            </DialogTitle>
          </DialogHeader>
          {publish && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Livello</Label>
                <Select value={publish.level} onValueChange={(v) => setPublish({ ...publish, level: v })}>
                  <SelectTrigger className="bg-card" data-testid="publish-level-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card">
                    {LEVELS.map((l) => (
                      <SelectItem key={l.id} value={l.id} data-testid={`publish-level-${l.id}`}>{l.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Categoria</Label>
                <Select value={publish.category} onValueChange={(v) => setPublish({ ...publish, category: v })}>
                  <SelectTrigger className="bg-card" data-testid="publish-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card">
                    {PROBLEMS.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Domanda (titolo FAQ)</Label>
                <Input
                  value={publish.question}
                  onChange={(e) => setPublish({ ...publish, question: e.target.value })}
                  data-testid="publish-question-input"
                  className="focus-visible:ring-primary"
                />
              </div>
              <div className="space-y-2">
                <Label>Risposta *</Label>
                <Textarea
                  value={publish.answer}
                  onChange={(e) => setPublish({ ...publish, answer: e.target.value })}
                  rows={5}
                  data-testid="publish-answer-textarea"
                  placeholder="Scrivi la risposta che verrà pubblicata…"
                  className="focus-visible:ring-primary"
                />
              </div>
              <div className="space-y-2">
                <Label>Tag (separati da virgola, opzionale)</Label>
                <Input
                  value={publish.tags}
                  onChange={(e) => setPublish({ ...publish, tags: e.target.value })}
                  placeholder="es. colla, tenuta, estate"
                  data-testid="publish-tags-input"
                  className="focus-visible:ring-primary"
                />
              </div>
              {faqs.filter((f) => f.id !== publish.editId).length > 0 && (
                <div className="space-y-2">
                  <Label>Domande correlate (opzionale)</Label>
                  <div className="max-h-32 space-y-1 overflow-y-auto rounded-lg border border-border p-3" data-testid="publish-related-list">
                    {faqs
                      .filter((f) => f.id !== publish.editId)
                      .map((f) => (
                        <label key={f.id} className="flex cursor-pointer items-start gap-2 text-xs text-muted-foreground transition-colors duration-200 hover:text-foreground">
                          <input
                            type="checkbox"
                            className="mt-0.5 accent-primary"
                            checked={publish.relatedIds.includes(f.id)}
                            onChange={(e) =>
                              setPublish({
                                ...publish,
                                relatedIds: e.target.checked
                                  ? [...publish.relatedIds, f.id]
                                  : publish.relatedIds.filter((x) => x !== f.id),
                              })
                            }
                            data-testid={`publish-related-${f.id}`}
                          />
                          {f.question}
                        </label>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPublish(null)} data-testid="publish-cancel-button">Annulla</Button>
            <Button onClick={publishFaq} disabled={!publish?.answer?.trim()} data-testid="publish-confirm-button">
              Pubblica FAQ
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog strumento */}
      <Dialog open={!!toolEdit} onOpenChange={(o) => !o && setToolEdit(null)}>
        <DialogContent className="bg-card sm:max-w-lg" data-testid="tool-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Strumento</DialogTitle>
          </DialogHeader>
          {toolEdit && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Nome</Label>
                <Input
                  value={toolEdit.name}
                  onChange={(e) => setToolEdit({ ...toolEdit, name: e.target.value })}
                  data-testid="tool-name-input"
                  className="focus-visible:ring-primary"
                />
              </div>
              <div className="space-y-2">
                <Label>A cosa serve</Label>
                <Textarea
                  value={toolEdit.purpose}
                  onChange={(e) => setToolEdit({ ...toolEdit, purpose: e.target.value })}
                  rows={2}
                  data-testid="tool-purpose-textarea"
                  className="focus-visible:ring-primary"
                />
              </div>
              <div className="space-y-2">
                <Label>Quando e come utilizzarlo</Label>
                <Textarea
                  value={toolEdit.usage}
                  onChange={(e) => setToolEdit({ ...toolEdit, usage: e.target.value })}
                  rows={2}
                  data-testid="tool-usage-textarea"
                  className="focus-visible:ring-primary"
                />
              </div>
              <div className="space-y-2">
                <Label>Cosa evitare</Label>
                <Textarea
                  value={toolEdit.avoid}
                  onChange={(e) => setToolEdit({ ...toolEdit, avoid: e.target.value })}
                  rows={2}
                  data-testid="tool-avoid-textarea"
                  className="focus-visible:ring-primary"
                />
              </div>
              {faqs.length > 0 && (
                <div className="space-y-2">
                  <Label>Domande correlate (opzionale)</Label>
                  <div className="max-h-32 space-y-1 overflow-y-auto rounded-lg border border-border p-3" data-testid="tool-related-list">
                    {faqs.map((f) => (
                      <label key={f.id} className="flex cursor-pointer items-start gap-2 text-xs text-muted-foreground transition-colors duration-200 hover:text-foreground">
                        <input
                          type="checkbox"
                          className="mt-0.5 accent-primary"
                          checked={toolEdit.relatedIds.includes(f.id)}
                          onChange={(e) =>
                            setToolEdit({
                              ...toolEdit,
                              relatedIds: e.target.checked
                                ? [...toolEdit.relatedIds, f.id]
                                : toolEdit.relatedIds.filter((x) => x !== f.id),
                            })
                          }
                          data-testid={`tool-related-${f.id}`}
                        />
                        {f.question}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setToolEdit(null)} data-testid="tool-cancel-button">Annulla</Button>
            <Button onClick={saveTool} data-testid="tool-save-button">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
